import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import {
  FirebaseMessaging,
  Importance,
  Visibility,
  type Notification,
} from '@capacitor-firebase/messaging';
import { LocalNotifications } from '@capacitor/local-notifications';
import type { PluginListenerHandle } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { debugInfo } from '@/utils/logger';

type PushPlatform = 'ios' | 'android' | 'web';

type PushData = Record<string, unknown> & {
  type?: string;
  chat_id?: string;
  chatId?: string;
  request_id?: string;
  requestId?: string;
  match_id?: string;
  matchId?: string;
  notification_id?: string;
  notificationId?: string;
  sender_id?: string;
  senderId?: string;
};

type PushDebugResult = 'idle' | 'success' | 'skipped' | 'error';

export interface PushDebugSnapshot {
  enabled: boolean;
  platform: string;
  native: boolean;
  activeUserPresent: boolean;
  permissionStatus: string | null;
  currentFcmTokenPresent: boolean;
  storedTokenPresent: boolean;
  deviceTokenRowCreated: boolean | null;
  lastSeenUpdateResult: PushDebugResult;
  logoutRevokeResult: PushDebugResult;
  lastError: string | null;
  updatedAt: string | null;
}

const TOKEN_STORAGE_KEY = 'blissnet_push_token';
const USER_STORAGE_KEY = 'blissnet_push_user_id';
// V600: Dedicated key for the active user id used by self-notification filtering.
// Kept separate from USER_STORAGE_KEY (which is coupled to token-ownership checks
// inside readStoredToken) so that a switch-user without a fresh FCM token cannot
// accidentally surface a previous user's token.
const ACTIVE_USER_STORAGE_KEY = 'blissnet_push_active_user';
const TOKEN_HISTORY_STORAGE_KEY_PREFIX = 'blissnet_push_token_history';
const MAX_DEDUP_AGE_MS = 8000;
const PUSH_DEBUG_ENABLED = import.meta.env.DEV === true || import.meta.env.VITE_PUSH_DEBUG === 'true';
const WEB_PUSH_SW_PATH = '/firebase-messaging-sw.js';

declare global {
  interface Window {
    blissnetPushDebug?: () => PushDebugSnapshot;
  }
}

class PushService {
  private activeUserId: string | null = null;
  private currentToken: string | null = null;
  private didInitListeners = false;
  private didCreateChannels = false;
  private listenerHandles: PluginListenerHandle[] = [];
  private appStateListener: PluginListenerHandle | null = null;
  private recentNotificationMap = new Map<string, number>();
  private appIsActive = true;
  private localNotificationPermissionGranted = false;
  private debugState: PushDebugSnapshot = this.createInitialDebugState();

  constructor() {
    if (PUSH_DEBUG_ENABLED && typeof window !== 'undefined') {
      window.blissnetPushDebug = () => this.getDebugSnapshot();
      this.logDebug('debug helper installed; run window.blissnetPushDebug()');
    }
  }

  async initializeForUser(userId: string): Promise<void> {
    if (!userId) {
      this.updateDebug({ lastSeenUpdateResult: 'skipped', lastError: 'init skipped: missing user' });
      return;
    }

    this.activeUserId = userId;
    this.writeActiveUserId(userId);
    this.currentToken = this.readStoredToken(userId);
    debugInfo('[Push] init start', {
      platform: this.getPlatform(),
      uid: userId.slice(0, 8),
      hasStoredToken: !!this.currentToken,
    });
    this.updateDebug({
      activeUserPresent: true,
      platform: this.getPlatform(),
      native: Capacitor.isNativePlatform(),
      storedTokenPresent: !!this.currentToken,
      currentFcmTokenPresent: !!this.currentToken,
      lastError: null,
    });

    if (Capacitor.isNativePlatform()) {
      await this.ensureListeners();
      await this.ensureAndroidChannels();
      await this.ensureResumeRefresh();
    }
  }

  async checkNotificationPermission(): Promise<'granted' | 'denied' | 'prompt'> {
    if (Capacitor.isNativePlatform()) {
      const current = await FirebaseMessaging.checkPermissions();
      this.updateDebug({ permissionStatus: current.receive });
      if (current.receive === 'granted') return 'granted';
      if (current.receive === 'denied') return 'denied';
      return 'prompt';
    }

    if (typeof window !== 'undefined' && 'Notification' in window) {
      const permission = Notification.permission;
      if (permission === 'granted' || permission === 'denied') return permission;
      return 'prompt';
    }

    return 'denied';
  }

  async requestPermissionAndRegister(userId: string): Promise<'granted' | 'denied'> {
    if (!userId) return 'denied';

    await this.initializeForUser(userId);

    try {
      if (Capacitor.isNativePlatform()) {
        const permission = await this.ensurePermission();
        debugInfo('[Push] consent permission', permission);
        this.updateDebug({ permissionStatus: permission });
        if (permission !== 'granted') {
          this.updateDebug({
            deviceTokenRowCreated: false,
            lastSeenUpdateResult: 'skipped',
            lastError: 'permission not granted',
          });
          return 'denied';
        }

        await this.ensureLocalNotificationPermission();

        const { token } = await FirebaseMessaging.getToken();
        debugInfo('[Push] getToken result', { hasToken: !!token, length: token?.length ?? 0 });
        this.updateDebug({ currentFcmTokenPresent: !!token });
        await this.upsertToken(userId, token);
        return 'granted';
      }

      const webPermission = await this.ensureWebPermission();
      this.updateDebug({ permissionStatus: webPermission });
      if (webPermission !== 'granted') {
        return 'denied';
      }

      await this.registerWebToken(userId);
      return 'granted';
    } catch (error) {
      console.error('[Push] consent register failed', this.formatError(error));
      this.updateDebug({
        deviceTokenRowCreated: false,
        currentFcmTokenPresent: false,
        lastSeenUpdateResult: 'error',
        lastError: this.formatError(error),
      });
      return 'denied';
    }
  }

  /** @deprecated Use initializeForUser + requestPermissionAndRegister after explicit user opt-in */
  async registerForUser(userId: string): Promise<void> {
    await this.initializeForUser(userId);
  }

  async unregisterCurrentToken(userId?: string | null): Promise<void> {
    const targetUserId = userId ?? this.activeUserId;
    let token = targetUserId ? this.readStoredToken(targetUserId) : this.currentToken;

    try {
      if (!Capacitor.isNativePlatform() && targetUserId) {
        await this.unregisterWebToken(targetUserId, token);
        return;
      }

      if (Capacitor.isNativePlatform() && !token) {
        token = (await FirebaseMessaging.getToken()).token;
      }

      if (targetUserId && token) {
        const { error, data } = await (supabase as any)
          .from('device_tokens')
          .update({
            revoked_at: new Date().toISOString(),
            last_seen_at: new Date().toISOString(),
          })
          .eq('user_id', targetUserId)
          .eq('token', token)
          .select('id, revoked_at')
          .maybeSingle();

        if (error) {
          this.updateDebug({ logoutRevokeResult: 'error', lastError: error.message ?? 'logout revoke failed' });
        } else {
          this.updateDebug({ logoutRevokeResult: data?.revoked_at ? 'success' : 'skipped', lastError: null });
        }
      } else {
        this.updateDebug({ logoutRevokeResult: 'skipped', lastError: 'no token to revoke' });
      }

      if (Capacitor.isNativePlatform()) {
        await FirebaseMessaging.deleteToken();
      }
    } catch (error) {
      this.updateDebug({ logoutRevokeResult: 'error', lastError: this.formatError(error) });
      console.warn('[Push] Token unregister failed:', error);
    } finally {
      this.clearStoredToken(targetUserId ?? undefined);
      this.clearActiveUserId();
      this.currentToken = null;
      this.activeUserId = null;
      this.updateDebug({
        activeUserPresent: false,
        currentFcmTokenPresent: false,
        storedTokenPresent: false,
      });
    }
  }

  async refreshLastSeen(userId?: string | null): Promise<void> {
    const targetUserId = userId ?? this.activeUserId;
    const token = targetUserId ? this.readStoredToken(targetUserId) : this.currentToken;
    if (!targetUserId || !token) {
      this.updateDebug({ lastSeenUpdateResult: 'skipped', lastError: 'last_seen skipped: no active token' });
      return;
    }

    const { error, data } = await (supabase as any)
      .from('device_tokens')
      .update({
        last_seen_at: new Date().toISOString(),
        app_version: this.getAppVersion(),
      })
      .eq('user_id', targetUserId)
      .eq('token', token)
      .is('revoked_at', null)
      .select('id, last_seen_at')
      .maybeSingle();

    if (error) {
      this.updateDebug({ lastSeenUpdateResult: 'error', lastError: error.message ?? 'last_seen update failed' });
      console.warn('[Push] last_seen_at refresh failed:', error);
      return;
    }

    this.updateDebug({
      lastSeenUpdateResult: data?.last_seen_at ? 'success' : 'skipped',
      lastError: data?.last_seen_at ? null : 'last_seen update matched no active row',
    });
  }

  private async registerWebToken(userId: string): Promise<void> {
    const webSupported = this.isWebPushSupported();
    if (!webSupported.ok) {
      const permissionStatus =
        typeof window !== 'undefined' && 'Notification' in window
          ? Notification.permission
          : null;
      this.updateDebug({
        permissionStatus,
        deviceTokenRowCreated: false,
        lastSeenUpdateResult: 'skipped',
        lastError: webSupported.reason,
      });
      debugInfo('[Push] web registration skipped', { reason: webSupported.reason });
      return;
    }

    const permission = await this.ensureWebPermission();
    this.updateDebug({ permissionStatus: permission });
    if (permission !== 'granted') {
      this.updateDebug({
        deviceTokenRowCreated: false,
        lastSeenUpdateResult: 'skipped',
        lastError: permission === 'denied' ? 'web permission denied' : 'web permission not granted',
      });
      console.warn('[Push] Web push permission not granted, token registration skipped');
      return;
    }

    const firebaseConfig = this.readWebFirebaseConfig();
    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY?.trim();
    if (!firebaseConfig || !vapidKey) {
      this.updateDebug({
        deviceTokenRowCreated: false,
        lastSeenUpdateResult: 'skipped',
        lastError: 'web push env missing',
      });
      console.warn('[Push] Web push env missing, token registration skipped');
      return;
    }

    const swRegistration = await navigator.serviceWorker.register(
      this.buildWebPushServiceWorkerUrl(firebaseConfig),
      { scope: '/' }
    );
    await navigator.serviceWorker.ready;

    const [{ getApps, getApp, initializeApp }, { getMessaging, getToken, isSupported }] =
      await Promise.all([
        import('firebase/app'),
        import('firebase/messaging'),
      ]);

    if (!(await isSupported())) {
      this.updateDebug({
        deviceTokenRowCreated: false,
        lastSeenUpdateResult: 'skipped',
        lastError: 'firebase messaging unsupported browser',
      });
      return;
    }

    const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
    const messaging = getMessaging(app);
    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: swRegistration,
    });

    debugInfo('[Push] web getToken result', { hasToken: !!token, length: token?.length ?? 0 });
    this.updateDebug({ currentFcmTokenPresent: !!token });
    await this.upsertToken(userId, token);
  }

  private async unregisterWebToken(userId: string, token: string | null): Promise<void> {
    if (!token) {
      this.updateDebug({ logoutRevokeResult: 'skipped', lastError: 'no token to revoke' });
      return;
    }

    const { error, data } = await (supabase as any)
      .from('device_tokens')
      .update({
        revoked_at: new Date().toISOString(),
        last_seen_at: new Date().toISOString(),
      })
      .eq('user_id', userId)
      .eq('token', token)
      .select('id, revoked_at')
      .maybeSingle();

    if (error) {
      this.updateDebug({ logoutRevokeResult: 'error', lastError: error.message ?? 'logout revoke failed' });
      return;
    }

    this.updateDebug({ logoutRevokeResult: data?.revoked_at ? 'success' : 'skipped', lastError: null });

    try {
      const firebaseConfig = this.readWebFirebaseConfig();
      if (!firebaseConfig) return;
      const [{ getApps, getApp, initializeApp }, { getMessaging, deleteToken, isSupported }] =
        await Promise.all([
          import('firebase/app'),
          import('firebase/messaging'),
        ]);

      if (!(await isSupported())) return;
      const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
      const messaging = getMessaging(app);
      await deleteToken(messaging);
    } catch (error) {
      console.warn('[Push] Web token delete failed:', this.formatError(error));
    }
  }

  getDebugSnapshot(): PushDebugSnapshot {
    const storedToken = this.activeUserId ? this.readStoredToken(this.activeUserId) : null;
    return {
      ...this.debugState,
      enabled: PUSH_DEBUG_ENABLED,
      platform: Capacitor.getPlatform(),
      native: Capacitor.isNativePlatform(),
      activeUserPresent: !!this.activeUserId,
      currentFcmTokenPresent: !!this.currentToken,
      storedTokenPresent: !!storedToken,
      updatedAt: new Date().toISOString(),
    };
  }

  private async ensureListeners(): Promise<void> {
    if (this.didInitListeners) return;
    this.didInitListeners = true;

    const tokenListener = await FirebaseMessaging.addListener('tokenReceived', ({ token }) => {
      if (!this.activeUserId || !token) return;
      void this.upsertToken(this.activeUserId, token);
    });

    const receivedListener = await FirebaseMessaging.addListener('notificationReceived', ({ notification }) => {
      const hydratedNotification = this.ensureDisplayPayload(notification);
      if (this.isSelfNotification(hydratedNotification)) return;
      if (this.isDuplicate(hydratedNotification, 'received')) return;
      void this.maybeShowAndroidForegroundFallback(hydratedNotification);
    });

    const actionListener = await FirebaseMessaging.addListener('notificationActionPerformed', ({ notification }) => {
      if (this.isSelfNotification(notification)) return;
      if (this.isDuplicate(notification, 'action')) return;
      this.routeNotification(notification);
    });

    const localActionListener = await LocalNotifications.addListener('localNotificationActionPerformed', ({ notification }) => {
      const syntheticNotification: Notification = {
        id: notification.id ? String(notification.id) : undefined,
        title: notification.title,
        body: notification.body,
        data: notification.extra ?? {},
      };
      if (this.isSelfNotification(syntheticNotification)) return;
      if (this.isDuplicate(syntheticNotification, 'action')) return;
      this.routeNotification(syntheticNotification);
    });

    this.listenerHandles.push(tokenListener, receivedListener, actionListener, localActionListener);
  }

  private async ensureAndroidChannels(): Promise<void> {
    if (this.didCreateChannels || Capacitor.getPlatform() !== 'android') return;
    this.didCreateChannels = true;

    await Promise.all([
      FirebaseMessaging.createChannel({
        id: 'messages',
        name: 'Messages',
        description: 'Chat message notifications',
        importance: Importance.High,
        visibility: Visibility.Private,
        vibration: true,
      }),
      FirebaseMessaging.createChannel({
        id: 'social',
        name: 'Social',
        description: 'Friend requests, accepts, and matches',
        importance: Importance.Default,
        visibility: Visibility.Private,
        vibration: true,
      }),
      FirebaseMessaging.createChannel({
        id: 'system',
        name: 'System',
        description: 'Account and service notifications',
        importance: Importance.Default,
        visibility: Visibility.Private,
        vibration: false,
      }),
    ]);
  }

  private async ensureResumeRefresh(): Promise<void> {
    if (this.appStateListener) return;

    try {
      const state = await CapApp.getState();
      this.appIsActive = !!state?.isActive;
    } catch {
      this.appIsActive = true;
    }

    if (this.appIsActive) {
      void this.clearAndroidDeliveredNotifications();
    }

    this.appStateListener = await CapApp.addListener('appStateChange', ({ isActive }) => {
      this.appIsActive = !!isActive;
      if (isActive) {
        void this.refreshLastSeen();
        void this.clearAndroidDeliveredNotifications();
      }
    });
  }

  // V600: Android-only — clear delivered FCM + local-fallback notifications when
  // the app becomes active. Mirrors Gmail/WhatsApp behavior: the launcher count
  // and shade entries clear on app open while the DB-backed unread state is
  // untouched (chats remain unread until the user actually reads them).
  // Idempotent + throttled to avoid loops on rapid resume/pause cycles.
  private androidDeliveredClearedAt = 0;
  private async clearAndroidDeliveredNotifications(): Promise<void> {
    if (Capacitor.getPlatform() !== 'android') return;
    const now = Date.now();
    if (now - this.androidDeliveredClearedAt < 1500) return;
    this.androidDeliveredClearedAt = now;

    try {
      await FirebaseMessaging.removeAllDeliveredNotifications();
    } catch (error) {
      console.warn('[Push] Android FCM delivered cleanup failed:', this.formatError(error));
    }
    try {
      await LocalNotifications.removeAllDeliveredNotifications();
    } catch (error) {
      console.warn('[Push] Android local delivered cleanup failed:', this.formatError(error));
    }
  }

  private async ensurePermission(): Promise<string> {
    const current = await FirebaseMessaging.checkPermissions();
    this.updateDebug({ permissionStatus: current.receive });
    if (current.receive === 'granted') return current.receive;

    const requested = await FirebaseMessaging.requestPermissions();
    this.updateDebug({ permissionStatus: requested.receive });
    return requested.receive;
  }

  private async ensureLocalNotificationPermission(): Promise<void> {
    if (Capacitor.getPlatform() !== 'android') return;

    try {
      const current = await LocalNotifications.checkPermissions();
      if (current.display === 'granted') {
        this.localNotificationPermissionGranted = true;
        return;
      }

      const requested = await LocalNotifications.requestPermissions();
      this.localNotificationPermissionGranted = requested.display === 'granted';
      if (!this.localNotificationPermissionGranted) {
        console.warn('[Push] Local notification permission not granted; foreground fallback disabled');
      }
    } catch (error) {
      this.localNotificationPermissionGranted = false;
      console.warn('[Push] Local notification permission check failed:', this.formatError(error));
    }
  }

  private async upsertToken(userId: string, token: string): Promise<void> {
    if (!userId || !token) return;

    const now = new Date().toISOString();
    const platform = this.getPlatform();
    const knownLineageTokens = this.getKnownLineageTokens(userId, platform)
      .filter((known) => known && known !== token);

    if (knownLineageTokens.length > 0) {
      const { error: revokeLineageError, data: revokedRows } = await (supabase as any)
        .from('device_tokens')
        .update({
          revoked_at: now,
          last_seen_at: now,
        })
        .eq('user_id', userId)
        .eq('platform', platform)
        .is('revoked_at', null)
        .in('token', knownLineageTokens)
        .select('id');

      if (revokeLineageError) {
        console.warn('[Push] Same-device lineage revoke failed:', {
          platform,
          tokens: knownLineageTokens.length,
          error: revokeLineageError,
        });
      } else if ((revokedRows?.length ?? 0) > 0) {
        debugInfo('[Push] Same-device stale tokens revoked', {
          platform,
          revoked: revokedRows?.length ?? 0,
        });
      }
    }

    let revokeError: any = null;
    for (let attempt = 1; attempt <= 2; attempt += 1) {
      const revokeResult = await (supabase as any)
        .from('device_tokens')
        .update({
          revoked_at: now,
          last_seen_at: now,
        })
        .eq('token', token)
        .neq('user_id', userId)
        .is('revoked_at', null);
      revokeError = revokeResult.error ?? null;
      if (!revokeError) break;
      console.warn('[Push] Previous token owner revoke attempt failed:', {
        attempt,
        error: revokeError,
      });
    }
    if (revokeError) {
      this.updateDebug({
        deviceTokenRowCreated: false,
        lastError: 'cross-user token revoke failed',
      });
      throw new Error('Cross-user token revoke failed; registration aborted');
    }

    const { error } = await (supabase as any)
      .from('device_tokens')
      .upsert(
        {
          user_id: userId,
          token,
          platform,
          app_version: this.getAppVersion(),
          last_seen_at: now,
          revoked_at: null,
        },
        { onConflict: 'user_id,token' }
      );

    if (error) {
      this.updateDebug({ deviceTokenRowCreated: false, lastError: error.message ?? 'token upsert failed' });
      console.error('[Push] device_tokens upsert failed', {
        code: (error as any).code ?? null,
        message: error.message ?? 'unknown',
      });
      throw new Error(error.message ?? 'device_tokens upsert failed');
    }

    debugInfo('[Push] device_tokens upsert ok', { tokenLen: token.length });
    this.currentToken = token;
    this.writeStoredToken(userId, token);
    this.writeTokenHistory(userId, platform, token);
    await this.verifyActiveTokenRow(userId, token);
  }

  private async verifyActiveTokenRow(userId: string, token: string): Promise<void> {
    const { data, error } = await (supabase as any)
      .from('device_tokens')
      .select('id, last_seen_at, revoked_at')
      .eq('user_id', userId)
      .eq('token', token)
      .is('revoked_at', null)
      .maybeSingle();

    if (error) {
      console.error('[Push] verify active row failed', error.message ?? 'unknown');
      this.updateDebug({ deviceTokenRowCreated: false, lastError: error.message ?? 'token row verification failed' });
      return;
    }

    debugInfo('[Push] verify active row', { hasRow: !!data?.id });
    this.updateDebug({
      deviceTokenRowCreated: !!data?.id,
      lastSeenUpdateResult: data?.last_seen_at ? 'success' : 'skipped',
      lastError: data?.id ? null : 'token row verification matched no active row',
    });
  }

  private routeNotification(notification: Notification): void {
    const data = this.normalizeData(notification.data);
    const path = this.pathFromData(data);
    if (!path) return;

    try {
      localStorage.setItem('blissnet_pending_push_path', path);
      // V600: Persist normalized payload so cold-start consumers (App.tsx) can
      // recover sender_id and seed ChatConversation peer hydration before the
      // chat row roundtrip finishes. Cleared by App.tsx after consumption.
      localStorage.setItem('blissnet_pending_push_data', JSON.stringify(data));
    } catch {
      // Storage can fail in private modes; the immediate event still handles warm starts.
    }

    window.dispatchEvent(new CustomEvent('blissnet-push-navigation', {
      detail: { path, data },
    }));
  }

  private async maybeShowAndroidForegroundFallback(notification: Notification): Promise<void> {
    if (Capacitor.getPlatform() !== 'android' || !this.appIsActive) return;
    if (!this.localNotificationPermissionGranted) return;
    if (!this.shouldUseAndroidForegroundFallback(notification)) return;
    if (this.isDuplicate(notification, 'local')) return;

    try {
      if (await this.hasDeliveredTwin(notification)) {
        return;
      }

      const data = this.normalizeData(notification.data);
      const now = Date.now();
      const id = now % 2147483647;
      const title = this.stringValue(notification.title) ?? 'BlissNet notification';
      const body = this.stringValue(notification.body) ?? 'Open BlissNet to view details.';

      await LocalNotifications.schedule({
        notifications: [{
          id,
          title,
          body,
          channelId: this.channelIdForData(data),
          extra: data as Record<string, unknown>,
          schedule: { at: new Date(now + 50) },
        }],
      });
    } catch (error) {
      console.warn('[Push] Android foreground fallback notification failed:', this.formatError(error));
    }
  }

  private shouldUseAndroidForegroundFallback(notification: Notification): boolean {
    // FirebaseMessaging listener docs: on Android, `notificationReceived` fires for all foreground pushes.
    // Foreground notifications are commonly delivered without tray presentation, so we schedule local fallback.
    return this.stringValue(notification.title) !== null || this.stringValue(notification.body) !== null;
  }

  private async hasDeliveredTwin(notification: Notification): Promise<boolean> {
    try {
      const delivered = await FirebaseMessaging.getDeliveredNotifications();
      const data = this.normalizeData(notification.data);
      const incomingKey = String(data.notification_id ?? data.notificationId ?? notification.id ?? notification.tag ?? '');
      const incomingTitle = this.stringValue(notification.title);
      const incomingBody = this.stringValue(notification.body);

      return delivered.notifications.some((item) => {
        const itemData = this.normalizeData(item.data);
        const itemKey = String(itemData.notification_id ?? itemData.notificationId ?? item.id ?? item.tag ?? '');
        if (incomingKey && itemKey && incomingKey === itemKey) return true;
        return this.stringValue(item.title) === incomingTitle && this.stringValue(item.body) === incomingBody;
      });
    } catch {
      return false;
    }
  }

  private channelIdForData(data: PushData): string {
    const type = String(data.type ?? '');
    if (type === 'message') return 'messages';
    if (type === 'friend_request' || type === 'friend_accept' || type === 'match') return 'social';
    return 'system';
  }

  private pathFromData(data: PushData): string | null {
    const type = String(data.type ?? '');
    const chatId = data.chat_id ?? data.chatId;
    const requestId = data.request_id ?? data.requestId;
    const matchId = data.match_id ?? data.matchId;

    if (type === 'message' && typeof chatId === 'string' && chatId) {
      return `/chat/${encodeURIComponent(chatId)}`;
    }

    if (type === 'friend_request') {
      return requestId
        ? `/network?tab=requests&requestId=${encodeURIComponent(String(requestId))}`
        : '/network?tab=requests';
    }

    if (type === 'friend_accept') {
      return '/network?tab=first';
    }

    if (type === 'match') {
      return matchId
        ? `/network?tab=favorites&matchId=${encodeURIComponent(String(matchId))}`
        : '/network?tab=favorites';
    }

    return null;
  }

  private isDuplicate(notification: Notification, source: 'received' | 'action' | 'local'): boolean {
    const now = Date.now();
    for (const [key, ts] of this.recentNotificationMap.entries()) {
      if (now - ts > MAX_DEDUP_AGE_MS) {
        this.recentNotificationMap.delete(key);
      }
    }

    const data = this.normalizeData(notification.data);
    const baseKey = String(
      data.notification_id ??
      data.notificationId ??
      notification.id ??
      notification.tag ??
      `${data.type ?? 'unknown'}:${data.chat_id ?? data.chatId ?? data.request_id ?? data.requestId ?? data.match_id ?? data.matchId ?? notification.title ?? ''}:${notification.body ?? ''}`
    );
    const key = `${source}:${baseKey}`;

    const previous = this.recentNotificationMap.get(key);
    if (previous && now - previous <= MAX_DEDUP_AGE_MS) {
      return true;
    }

    this.recentNotificationMap.set(key, now);
    return false;
  }

  private ensureDisplayPayload(notification: Notification): Notification {
    const data = this.normalizeData(notification.data);
    const fallbackTitle = this.stringValue(
      data.title ?? data.notification_title ?? data.notificationTitle ?? data.sender_name ?? data.senderName
    );
    const fallbackBody = this.stringValue(
      data.body ?? data.notification_body ?? data.notificationBody ?? data.message ?? data.content
    );

    const title = this.stringValue(notification.title) ?? fallbackTitle;
    const body = this.stringValue(notification.body) ?? fallbackBody;
    if (!title && !body) return notification;

    return {
      ...notification,
      title: title ?? notification.title,
      body: body ?? notification.body,
    };
  }

  private normalizeData(data: unknown): PushData {
    if (!data || typeof data !== 'object') return {};
    return data as PushData;
  }

  // Defense in depth: drop any push whose sender_id equals the active user.
  // The DB trigger already skips sender == recipient, but a stale device-token
  // ownership row can still cause a self-routed push to surface here.
  // V600: Falls back to persisted active user id so the guard still works on
  // cold-start when the action handler fires before registerForUser runs.
  private isSelfNotification(notification: Notification): boolean {
    const activeUserId = this.activeUserId ?? this.readActiveUserId();
    if (!activeUserId) return false;
    const data = this.normalizeData(notification.data);
    const senderId = this.stringValue((data as PushData).sender_id) ??
                     this.stringValue((data as Record<string, unknown>).senderId);
    if (!senderId) return false;
    return senderId === activeUserId;
  }

  private readActiveUserId(): string | null {
    try {
      return localStorage.getItem(ACTIVE_USER_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private writeActiveUserId(userId: string): void {
    try {
      localStorage.setItem(ACTIVE_USER_STORAGE_KEY, userId);
    } catch {
      // Non-fatal; in-memory activeUserId still works for the live session.
    }
  }

  private clearActiveUserId(): void {
    try {
      localStorage.removeItem(ACTIVE_USER_STORAGE_KEY);
    } catch {
      // Non-fatal.
    }
  }

  private stringValue(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  private getPlatform(): PushPlatform {
    const platform = Capacitor.getPlatform();
    return platform === 'ios' || platform === 'android' ? platform : 'web';
  }

  private isWebPushSupported(): { ok: boolean; reason: string | null } {
    if (Capacitor.isNativePlatform()) return { ok: false, reason: 'native platform' };
    if (typeof window === 'undefined') return { ok: false, reason: 'window unavailable' };
    if (!window.isSecureContext) return { ok: false, reason: 'insecure context' };
    if (!('Notification' in window)) return { ok: false, reason: 'notification api unavailable' };
    if (!('serviceWorker' in navigator)) return { ok: false, reason: 'service worker unavailable' };
    if (!('PushManager' in window)) return { ok: false, reason: 'push manager unavailable' };
    return { ok: true, reason: null };
  }

  private async ensureWebPermission(): Promise<NotificationPermission> {
    const current = Notification.permission;
    if (current === 'granted' || current === 'denied') return current;
    return Notification.requestPermission();
  }

  private readWebFirebaseConfig():
    | {
        apiKey: string;
        authDomain: string;
        projectId: string;
        storageBucket: string;
        messagingSenderId: string;
        appId: string;
      }
    | null {
    const apiKey = import.meta.env.VITE_FIREBASE_API_KEY?.trim();
    const authDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN?.trim();
    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID?.trim();
    const storageBucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET?.trim();
    const messagingSenderId = import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID?.trim();
    const appId = import.meta.env.VITE_FIREBASE_APP_ID?.trim();

    if (!apiKey || !authDomain || !projectId || !storageBucket || !messagingSenderId || !appId) {
      return null;
    }

    return {
      apiKey,
      authDomain,
      projectId,
      storageBucket,
      messagingSenderId,
      appId,
    };
  }

  private buildWebPushServiceWorkerUrl(config: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
  }): string {
    const params = new URLSearchParams({
      apiKey: config.apiKey,
      authDomain: config.authDomain,
      projectId: config.projectId,
      storageBucket: config.storageBucket,
      messagingSenderId: config.messagingSenderId,
      appId: config.appId,
    });

    return `${WEB_PUSH_SW_PATH}?${params.toString()}`;
  }

  private getAppVersion(): string | null {
    return import.meta.env.VITE_APP_VERSION ?? null;
  }

  private readStoredToken(userId: string): string | null {
    try {
      const storedUserId = localStorage.getItem(USER_STORAGE_KEY);
      if (storedUserId !== userId) return null;
      return localStorage.getItem(TOKEN_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private writeStoredToken(userId: string, token: string): void {
    try {
      localStorage.setItem(USER_STORAGE_KEY, userId);
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } catch {
      // Non-fatal; token refresh can still use the in-memory token.
    }
  }

  private readTokenHistory(userId: string, platform: PushPlatform): string[] {
    try {
      const key = `${TOKEN_HISTORY_STORAGE_KEY_PREFIX}:${userId}:${platform}`;
      const raw = localStorage.getItem(key);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((value) => typeof value === 'string' && value.length > 0);
    } catch {
      return [];
    }
  }

  private writeTokenHistory(userId: string, platform: PushPlatform, latestToken: string): void {
    try {
      const key = `${TOKEN_HISTORY_STORAGE_KEY_PREFIX}:${userId}:${platform}`;
      const existing = this.readTokenHistory(userId, platform);
      const merged = [latestToken, ...existing.filter((token) => token !== latestToken)].slice(0, 12);
      localStorage.setItem(key, JSON.stringify(merged));
    } catch {
      // Non-fatal best effort.
    }
  }

  private getKnownLineageTokens(userId: string, platform: PushPlatform): string[] {
    const storedToken = this.readStoredToken(userId);
    const candidates = [
      this.currentToken,
      storedToken,
      ...this.readTokenHistory(userId, platform),
    ].filter((value): value is string => typeof value === 'string' && value.length > 0);
    return Array.from(new Set(candidates));
  }

  private clearStoredToken(userId?: string): void {
    try {
      const storedUserId = localStorage.getItem(USER_STORAGE_KEY);
      if (userId && storedUserId && storedUserId !== userId) return;
      localStorage.removeItem(USER_STORAGE_KEY);
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem('blissnet_pending_push_path');
      localStorage.removeItem('blissnet_pending_push_data');
    } catch {
      // Non-fatal cleanup.
    }
  }

  private createInitialDebugState(): PushDebugSnapshot {
    return {
      enabled: PUSH_DEBUG_ENABLED,
      platform: Capacitor.getPlatform(),
      native: Capacitor.isNativePlatform(),
      activeUserPresent: false,
      permissionStatus: null,
      currentFcmTokenPresent: false,
      storedTokenPresent: false,
      deviceTokenRowCreated: null,
      lastSeenUpdateResult: 'idle',
      logoutRevokeResult: 'idle',
      lastError: null,
      updatedAt: null,
    };
  }

  private updateDebug(update: Partial<PushDebugSnapshot>): void {
    this.debugState = {
      ...this.debugState,
      ...update,
      platform: Capacitor.getPlatform(),
      native: Capacitor.isNativePlatform(),
      updatedAt: new Date().toISOString(),
    };

    this.logDebug(this.debugState);
  }

  private logDebug(message: unknown): void {
    if (!PUSH_DEBUG_ENABLED) return;
    console.info('[Push Debug]', message);
  }

  private formatError(error: unknown): string {
    if (error instanceof Error) return error.message;
    if (typeof error === 'string') return error;
    return 'unknown error';
  }
}

export const pushService = new PushService();
